import { buildRenderModel } from "./model.js";
import { renderChart, resolveIconHref, } from "./render.js";
import { injectStyles } from "./styles.js";
import { resolveStrings } from "./strings.js";
import { activityColor, countryColor, resolveTheme } from "./theme.js";
import { formatClock, formatDayLabel, formatDuration, formatDurationCompact, localDateKey } from "./time.js";
import { escapeHtml, SVG_NS, svgEl } from "./svg.js";
/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
const DEFAULT_VISIBILITY = {
    driver: true,
    vehicles: true,
    countries: true,
    outOfScope: true,
    ferry: true,
    noCard: true,
    totals: true,
};
/** Smallest visible window, as a fraction of the axis (≈ 8 minutes of a day). */
const MIN_SPAN = 0.006;
const FULL_VIEW = { from: 0, to: 1 };
const TOOLTIP_OFFSET = 14;
let instanceCounter = 0;
/* ------------------------------------------------------------------ */
/* Factory                                                             */
/* ------------------------------------------------------------------ */
export function createActivityChart(container, options) {
    if (!container || typeof container.appendChild !== "function") {
        throw new TypeError("createActivityChart: container must be an HTMLElement");
    }
    if (!options || typeof options !== "object") {
        throw new TypeError("createActivityChart: options are required");
    }
    return new ActivityChartImpl(container, options);
}
/* ------------------------------------------------------------------ */
/* Implementation                                                      */
/* ------------------------------------------------------------------ */
class ActivityChartImpl {
    constructor(container, options) {
        this.instanceId = ++instanceCounter;
        this.renderState = null;
        this.hits = new Map();
        this.svg = null;
        this.svgWidth = 0;
        this.view = { from: 0, to: 1 };
        this.dayViews = new Map();
        this.activeDayKey = null;
        this.selectedId = null;
        this.hoverKey = null;
        this.hoveredElement = null;
        this.hoverOutline = null;
        this.tooltipKey = null;
        this.brushMode = true;
        this.didDrag = false;
        this.frame = 0;
        /** Rendered annotation lists, keyed by the local date of the row. */
        this.annotationEls = new Map();
        this.navSvg = null;
        this.navDrag = null;
        this.pointers = new Map();
        this.drag = null;
        this.pinch = null;
        this.brushPointerId = null;
        this.brushStartX = 0;
        this.brushDayKey = null;
        this.brushRect = null;
        this.resizeObserver = null;
        /* ---------------- events: chrome ---------------- */
        this.onBarClick = (event) => {
            const target = event.target;
            if (!(target instanceof Element))
                return;
            const button = target.closest("button");
            if (!(button instanceof HTMLButtonElement))
                return;
            const toggle = button.dataset.toggle;
            if (toggle) {
                this.visibility = { ...this.visibility, [toggle]: !this.visibility[toggle] };
                this.updateChromeState();
                this.render();
                return;
            }
            switch (button.dataset.action) {
                case "zoom-in":
                    this.zoomBy(1.6);
                    break;
                case "zoom-out":
                    this.zoomBy(1 / 1.6);
                    break;
                case "reset":
                    this.resetView();
                    break;
                case "brush":
                    this.brushMode = !this.brushMode;
                    this.updateChromeState();
                    break;
                case "export-svg":
                    this.downloadSvg();
                    break;
                case "export-png":
                    void this.downloadPng();
                    break;
                default:
                    break;
            }
        };
        this.onWindowResize = () => {
            this.scheduleRender();
        };
        /* ---------------- events: pointer ---------------- */
        this.onPointerDown = (event) => {
            if (!this.options.interactive || !this.renderState)
                return;
            if (event.pointerType === "mouse" && event.button !== 0)
                return;
            this.didDrag = false;
            this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (this.options.mode === "stacked" && event.target instanceof Element) {
                const dayNav = event.target.closest("[data-day-nav]");
                if (dayNav) {
                    this.beginDayNav(event);
                    return;
                }
            }
            if (this.pointers.size === 2) {
                const points = [...this.pointers.values()];
                const first = points[0];
                const second = points[1];
                if (first && second) {
                    const distance = Math.hypot(first.x - second.x, first.y - second.y);
                    if (distance > 0) {
                        const dayKey = this.dayKeyAtClientY((first.y + second.y) / 2);
                        this.pinch = {
                            distance,
                            anchor: this.fractionAtClientX((first.x + second.x) / 2),
                            view: dayKey ? { ...this.dayViewFor(dayKey) } : { ...this.view },
                            dayKey,
                        };
                        if (dayKey)
                            this.activeDayKey = dayKey;
                    }
                }
                this.drag = null;
                return;
            }
            if (this.pointers.size > 2)
                return;
            const dayKey = this.dayKeyAtClientY(event.clientY);
            if (dayKey)
                this.activeDayKey = dayKey;
            if (this.brushMode && !event.shiftKey) {
                if (this.options.mode === "stacked" && !dayKey)
                    return;
                this.beginBrush(event);
                return;
            }
            if (this.options.mode === "stacked" && !dayKey)
                return;
            this.drag = {
                pointerId: event.pointerId,
                startX: event.clientX,
                view: dayKey ? { ...this.dayViewFor(dayKey) } : { ...this.view },
                dayKey,
                moved: false,
            };
            this.capturePointer(event.pointerId);
        };
        this.onPointerMove = (event) => {
            if (this.pointers.has(event.pointerId)) {
                this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            }
            if (this.pinch && this.pointers.size >= 2) {
                const points = [...this.pointers.values()];
                const first = points[0];
                const second = points[1];
                if (first && second && this.pinch.distance > 0) {
                    const distance = Math.hypot(first.x - second.x, first.y - second.y);
                    this.applyZoom(this.pinch.view, this.pinch.anchor, distance / this.pinch.distance);
                }
                return;
            }
            if (this.brushRect && this.brushPointerId === event.pointerId) {
                this.updateBrush(event.clientX);
                return;
            }
            if (this.navDrag?.source === "canvas" && this.navDrag.pointerId === event.pointerId) {
                this.applyNavDrag(event.clientX);
                return;
            }
            if (this.drag && this.drag.pointerId === event.pointerId) {
                const dx = event.clientX - this.drag.startX;
                if (!this.drag.moved && Math.abs(dx) > 4) {
                    this.drag.moved = true;
                    this.svg?.classList.add("is-grabbing");
                }
                if (this.drag.moved) {
                    const span = this.drag.view.to - this.drag.view.from;
                    const plotWidth = this.renderState?.plot.width ?? this.svgWidth;
                    let from = this.drag.view.from - (dx / Math.max(1, plotWidth)) * span;
                    from = clamp(from, 0, 1 - span);
                    this.commitView({ from, to: from + span }, this.drag.dayKey);
                }
                return;
            }
            this.handleHover(event);
        };
        this.onPointerUp = (event) => {
            this.pointers.delete(event.pointerId);
            if (this.pinch && this.pointers.size < 2)
                this.pinch = null;
            if (this.brushPointerId === event.pointerId) {
                this.finishBrush(event.clientX);
            }
            if (this.navDrag?.source === "canvas" && this.navDrag.pointerId === event.pointerId) {
                this.navDrag = null;
                this.didDrag = true;
                this.releasePointer(event.pointerId);
            }
            if (this.drag && this.drag.pointerId === event.pointerId) {
                this.didDrag = this.drag.moved;
                this.drag = null;
                this.svg?.classList.remove("is-grabbing");
            }
            this.releasePointer(event.pointerId);
        };
        this.onPointerLeave = () => {
            if (this.drag || this.pinch || this.brushRect)
                return;
            this.clearHover();
        };
        this.onDoubleClick = (event) => {
            if (!this.options.interactive)
                return;
            const dayKey = this.dayKeyAtClientY(event.clientY);
            if (dayKey)
                this.commitView(FULL_VIEW, dayKey);
            else
                this.commitView(FULL_VIEW);
        };
        /* ---------------- events: click / keyboard ---------------- */
        this.onClick = (event) => {
            if (!this.options.interactive)
                return;
            if (this.didDrag) {
                this.didDrag = false;
                return;
            }
            const info = this.hitFromPoint(event.clientX, event.clientY) ?? this.hitFromEvent(event);
            if (info?.hit.kind === "segment") {
                this.selectHit(info.hit);
            }
            else if (this.selectedId) {
                this.selectHit(null);
            }
        };
        this.onKeyDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element))
                return;
            if (event.key === "Escape") {
                this.hideTooltip();
                if (target instanceof SVGElement)
                    target.blur();
                return;
            }
            const focusable = target.closest("[data-hit][tabindex]");
            if (!focusable)
                return;
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                const info = this.hitFromEvent(event);
                if (info?.hit.kind === "segment")
                    this.selectHit(info.hit);
                return;
            }
            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                event.preventDefault();
                const nodes = Array.from(this.svg?.querySelectorAll('[data-hit][tabindex="0"]') ?? []);
                const index = nodes.indexOf(focusable);
                const next = nodes[index + (event.key === "ArrowRight" ? 1 : -1)];
                next?.focus();
            }
        };
        this.onFocusIn = (event) => {
            const info = this.hitFromEvent(event);
            if (!info)
                return;
            this.tooltipKey = info.id;
            this.tip.innerHTML = this.tooltipHtml(info.hit);
            this.tip.hidden = false;
            const element = event.target.closest("[data-hit]");
            const rect = element?.getBoundingClientRect();
            if (rect) {
                this.placeTooltip(rect.left + rect.width / 2, rect.top + rect.height / 2);
            }
        };
        this.onFocusOut = (event) => {
            const related = event.relatedTarget;
            if (related instanceof Node && this.canvas.contains(related))
                return;
            this.hideTooltip();
        };
        this.onNavPointerDown = (event) => {
            if (!this.options.interactive || !this.navSvg)
                return;
            if (event.pointerType === "mouse" && event.button !== 0)
                return;
            const target = event.target;
            if (!(target instanceof Element))
                return;
            if (!this.navSvg.contains(target))
                return;
            const span = this.view.to - this.view.from;
            if (span >= 0.999)
                return;
            const stacked = this.options.mode === "stacked";
            const navWidth = this.navSvgWidth();
            const handle = target.closest("[data-nav-handle]");
            const windowElement = target.closest("[data-nav-window]");
            const dayElement = target.closest("[data-nav-day]");
            let unitWidth = navWidth;
            if (stacked) {
                const measured = windowElement ?? dayElement;
                if (measured) {
                    unitWidth = Number(measured.getAttribute("data-nav-day-width")) || navWidth;
                }
            }
            let nextView = null;
            if (!handle && !windowElement) {
                if (stacked && dayElement) {
                    const rect = this.navSvg.getBoundingClientRect();
                    const svgX = ((event.clientX - rect.left) / Math.max(1, rect.width)) * navWidth;
                    const dayStart = Number(dayElement.getAttribute("data-nav-day-start")) || 0;
                    const dayWidth = Number(dayElement.getAttribute("data-nav-day-width")) || navWidth;
                    const local = clamp((svgX - dayStart) / Math.max(1, dayWidth), 0, 1);
                    const from = clamp(local - span / 2, 0, 1 - span);
                    nextView = { from, to: from + span };
                    unitWidth = dayWidth;
                }
                else {
                    const fraction = this.navFractionAt(event.clientX);
                    const from = clamp(fraction - span / 2, 0, 1 - span);
                    nextView = { from, to: from + span };
                }
                this.commitView(nextView);
            }
            this.navDrag = {
                source: "bar",
                pointerId: event.pointerId,
                mode: handle ? handle.getAttribute("data-nav-handle") : "pan",
                startClientX: event.clientX,
                unitWidth,
                startView: nextView ?? { ...this.view },
                dayKey: null,
            };
            try {
                this.nav.setPointerCapture(event.pointerId);
            }
            catch {
                /* best effort */
            }
        };
        this.onNavPointerMove = (event) => {
            if (!this.navDrag || this.navDrag.source !== "bar")
                return;
            if (this.navDrag.pointerId !== event.pointerId)
                return;
            this.applyNavDrag(event.clientX);
        };
        this.onNavPointerUp = (event) => {
            if (!this.navDrag || this.navDrag.source !== "bar" || this.navDrag.pointerId !== event.pointerId)
                return;
            this.navDrag = null;
            try {
                this.nav.releasePointerCapture(event.pointerId);
            }
            catch {
                /* best effort */
            }
        };
        this.onNavDoubleClick = () => {
            this.navDrag = null;
            this.resetView();
        };
        /* ---------------- wheel / pinch ---------------- */
        this.onWheel = (event) => {
            if (!this.options.interactive || !this.options.wheelZoom || !this.renderState)
                return;
            const dayKey = this.dayKeyAtClientY(event.clientY);
            if (this.options.mode === "stacked" && !dayKey)
                return;
            event.preventDefault();
            if (dayKey)
                this.activeDayKey = dayKey;
            const anchor = this.fractionAtClientX(event.clientX);
            const factor = clamp(Math.exp(-event.deltaY * 0.0015), 0.2, 5);
            this.applyZoom(dayKey ? this.dayViewFor(dayKey) : this.view, anchor, factor, dayKey);
        };
        injectStyles();
        assertTimeZone(options.timezone);
        this.element = container;
        this.raw = { ...options, data: options.data ?? { days: [] } };
        this.visibility = { ...DEFAULT_VISIBILITY, ...options.visibility };
        this.options = resolveOptions(this.raw);
        this.model = buildRenderModel(this.options.data, this.options.timezone);
        this.root = document.createElement("div");
        this.root.className = "tac";
        this.bar = document.createElement("div");
        this.bar.className = "tac-bar";
        this.legendEl = document.createElement("div");
        this.legendEl.className = "tac-legend";
        this.actionsEl = document.createElement("div");
        this.actionsEl.className = "tac-actions";
        this.bar.append(this.legendEl, this.actionsEl);
        this.scroll = document.createElement("div");
        this.scroll.className = "tac-scroll";
        this.scroll.tabIndex = 0;
        this.scroll.setAttribute("role", "region");
        this.scroll.setAttribute("aria-label", this.options.ariaLabel);
        this.canvas = document.createElement("div");
        this.canvas.className = "tac-canvas";
        this.annotationsLayer = document.createElement("div");
        this.annotationsLayer.className = "tac-annotations-layer";
        this.canvas.appendChild(this.annotationsLayer);
        this.scroll.appendChild(this.canvas);
        this.nav = document.createElement("div");
        this.nav.className = "tac-nav";
        this.nav.hidden = true;
        this.tip = document.createElement("div");
        this.tip.className = "tac-tip";
        this.tip.setAttribute("role", "tooltip");
        this.tip.hidden = true;
        this.resetEl = document.createElement("button");
        this.resetEl.type = "button";
        this.resetEl.className = "tac-reset-float";
        this.resetEl.textContent = this.options.strings.resetZoom;
        this.resetEl.title = this.options.strings.resetZoom;
        this.resetEl.setAttribute("aria-label", this.options.strings.resetZoom);
        this.resetEl.hidden = true;
        this.resetEl.addEventListener("click", () => this.resetView());
        this.root.append(this.bar, this.scroll, this.nav, this.tip, this.resetEl);
        container.appendChild(this.root);
        this.applyThemeVars();
        this.buildChrome();
        this.render();
        if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(() => this.scheduleRender());
            this.resizeObserver.observe(this.root);
        }
        else {
            window.addEventListener("resize", this.onWindowResize);
        }
        this.bar.addEventListener("click", this.onBarClick);
        this.canvas.addEventListener("pointerdown", this.onPointerDown);
        this.canvas.addEventListener("pointermove", this.onPointerMove);
        this.canvas.addEventListener("pointerup", this.onPointerUp);
        this.canvas.addEventListener("pointercancel", this.onPointerUp);
        this.canvas.addEventListener("pointerleave", this.onPointerLeave);
        this.canvas.addEventListener("click", this.onClick);
        this.canvas.addEventListener("dblclick", this.onDoubleClick);
        this.canvas.addEventListener("keydown", this.onKeyDown);
        this.canvas.addEventListener("focusin", this.onFocusIn);
        this.canvas.addEventListener("focusout", this.onFocusOut);
        this.canvas.addEventListener("wheel", this.onWheel, { passive: false });
        this.nav.addEventListener("pointerdown", this.onNavPointerDown);
        this.nav.addEventListener("pointermove", this.onNavPointerMove);
        this.nav.addEventListener("pointerup", this.onNavPointerUp);
        this.nav.addEventListener("pointercancel", this.onNavPointerUp);
        this.nav.addEventListener("dblclick", this.onNavDoubleClick);
        // The annotation list is a normal HTML island: its own controls must not
        // start a chart drag/selection or a zoom.
        const stop = (event) => event.stopPropagation();
        this.annotationsLayer.addEventListener("pointerdown", stop);
        this.annotationsLayer.addEventListener("click", stop);
        this.annotationsLayer.addEventListener("dblclick", stop);
    }
    /* ---------------- public API ---------------- */
    update(patch) {
        const nextTimezone = patch.timezone ?? this.raw.timezone;
        assertTimeZone(nextTimezone);
        const timezoneChanged = nextTimezone !== this.raw.timezone;
        const dataChanged = patch.data !== undefined;
        const modeChanged = patch.mode !== undefined && patch.mode !== this.options.mode;
        this.raw = {
            ...this.raw,
            ...patch,
            data: patch.data ?? this.raw.data,
            timezone: nextTimezone,
            strings: patch.strings ? { ...this.raw.strings, ...patch.strings } : this.raw.strings,
        };
        if (patch.visibility) {
            this.visibility = { ...this.visibility, ...patch.visibility };
        }
        this.options = resolveOptions(this.raw);
        if (dataChanged || timezoneChanged) {
            this.model = buildRenderModel(this.options.data, nextTimezone);
        }
        if (dataChanged || timezoneChanged || modeChanged) {
            this.view = { from: 0, to: 1 };
            this.dayViews.clear();
            this.activeDayKey = null;
            this.selectedId = null;
        }
        // New data changes the annotations, so their lists are rebuilt.
        if (dataChanged || timezoneChanged || modeChanged) {
            this.disposeAnnotationLists();
        }
        this.applyThemeVars();
        this.buildChrome();
        this.render();
    }
    setData(data) {
        this.update({ data });
    }
    setTimezone(timeZone) {
        this.update({ timezone: timeZone });
    }
    setMode(mode) {
        this.update({ mode });
    }
    setBarHeightMode(mode) {
        this.update({ barHeightMode: mode });
    }
    setCoDriverDisplay(display) {
        if (display !== this.options.coDriver)
            this.update({ coDriver: display });
    }
    setVisibility(visibility) {
        this.visibility = { ...this.visibility, ...visibility };
        this.updateChromeState();
        this.render();
    }
    getVisibility() {
        return { ...this.visibility };
    }
    getView() {
        if (this.options.mode === "stacked") {
            const key = this.activeDayKey ?? this.model.days[0]?.dateKey ?? null;
            const dayView = key ? this.dayViews.get(key) : undefined;
            return { ...(dayView ?? FULL_VIEW) };
        }
        return { ...this.view };
    }
    setView(view) {
        const base = this.getView();
        const from = clamp(view.from ?? base.from, 0, 1 - MIN_SPAN);
        const to = clamp(view.to ?? base.to, from + MIN_SPAN, 1);
        this.commitView({ from, to });
    }
    resetView() {
        this.commitView({ from: 0, to: 1 });
    }
    zoomToRange(from, to) {
        const a = this.fractionForInstant(from);
        const b = this.fractionForInstant(to);
        if (a === null || b === null)
            return;
        const low = clamp(Math.min(a, b), 0, 1);
        const high = clamp(Math.max(a, b), 0, 1);
        if (high - low < 0.002)
            return;
        const dayKey = this.options.mode === "stacked" ? this.activeDayKey ?? this.model.days[0]?.dateKey ?? null : null;
        this.commitView({ from: low, to: high }, dayKey);
    }
    clearSelection() {
        this.selectHit(null);
    }
    getSelection() {
        if (!this.selectedId)
            return null;
        const hit = this.hits.get(this.selectedId);
        return hit ? this.selectionFromHit(hit) : null;
    }
    exportSvg() {
        if (!this.svg)
            return "";
        const clone = this.svg.cloneNode(true);
        clone.querySelectorAll(".tac-hover-outline").forEach((element) => element.remove());
        clone.setAttribute("xmlns", SVG_NS);
        clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
        const xml = new XMLSerializer().serializeToString(clone);
        return `<?xml version="1.0" encoding="UTF-8"?>\n${xml}`;
    }
    downloadSvg(filename = "tacho-activity-chart.svg") {
        const text = this.exportSvg();
        if (!text)
            return;
        downloadBlob(new Blob([text], { type: "image/svg+xml;charset=utf-8" }), filename);
    }
    async downloadPng(filename = "tacho-activity-chart.png", scale = 2) {
        const text = this.exportSvg();
        if (!text || !this.svg)
            return;
        const width = Number(this.svg.getAttribute("width") ?? this.svgWidth);
        const height = Number(this.svg.getAttribute("height") ?? 0);
        if (!(width > 0) || !(height > 0))
            return;
        const ratio = Math.max(1, scale);
        const blob = new Blob([text], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        try {
            const image = await loadImage(url);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(height * ratio);
            const context = canvas.getContext("2d");
            if (!context)
                throw new Error("Canvas 2D context unavailable");
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            const png = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
            if (png)
                downloadBlob(png, filename);
        }
        finally {
            URL.revokeObjectURL(url);
        }
    }
    destroy() {
        window.removeEventListener("resize", this.onWindowResize);
        if (this.frame) {
            cancelAnimationFrame(this.frame);
            this.frame = 0;
        }
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.pointers.clear();
        this.hits.clear();
        this.hideTooltip();
        this.annotationEls.clear();
        this.root.remove();
    }
    /* ---------------- rendering ---------------- */
    render(layoutPass = 0) {
        const width = this.measureWidth();
        const renderOptions = {
            mode: this.options.mode,
            coDriver: this.options.coDriver,
            icons: this.options.icons,
            timezone: this.options.timezone,
            locale: this.options.locale,
            theme: this.options.theme,
            strings: this.options.strings,
            laneHeight: this.options.laneHeight,
            barHeightMode: this.options.barHeightMode,
            showSegmentLabels: this.options.showSegmentLabels,
            showDayMeta: this.options.showDayMeta,
            showRulers: this.options.showRulers,
            navigator: this.options.navigator,
            interactive: this.options.interactive,
            ariaLabel: this.options.ariaLabel,
        };
        const result = renderChart({
            model: this.model,
            options: renderOptions,
            view: this.view,
            dayViews: this.dayViews,
            visibility: this.visibility,
            selectedId: this.selectedId,
            width,
            idPrefix: String(this.instanceId),
            annotationHeights: this.annotationHeights(),
        });
        this.canvas.replaceChildren(result.svg, this.annotationsLayer);
        this.svg = result.svg;
        this.svgWidth = width;
        this.renderState = result;
        this.hits = result.hits;
        if (this.selectedId && !this.hits.has(this.selectedId))
            this.selectedId = null;
        this.hideTooltip();
        // Rendering the lists gives them a real height; when that changes, the
        // rows need one more layout pass so they make room for them.
        const remeasured = this.applyAnnotationLists(result.annotationSlots ?? [], width);
        if (remeasured && layoutPass < 2) {
            this.render(layoutPass + 1);
            return;
        }
        this.buildNavigator();
        this.scroll.setAttribute("aria-label", this.options.ariaLabel);
        this.updateChromeState();
    }
    /** Heights already measured, keyed by the local date of the row. */
    annotationHeights() {
        if (this.options.mode !== "stacked")
            return undefined;
        const heights = new Map();
        for (const [date, entry] of this.annotationEls) {
            if (entry.height > 0)
                heights.set(date, entry.height);
        }
        return heights;
    }
    /**
     * Renders/positions the annotation list of every stacked row and returns
     * `true` when a list was created or changed its height.
     */
    applyAnnotationLists(slots, width) {
        if (this.options.mode !== "stacked" || slots.length === 0) {
            if (this.annotationsLayer.childElementCount > 0)
                this.annotationsLayer.replaceChildren();
            return false;
        }
        let changed = false;
        const live = new Set();
        for (const slot of slots) {
            live.add(slot.date);
            const day = this.model.days[slot.index];
            if (!day || day.annotations.length === 0)
                continue;
            let entry = this.annotationEls.get(slot.date);
            if (!entry) {
                const host = document.createElement("div");
                host.className = "tac-row-annotations";
                host.dataset.date = slot.date;
                const list = this.buildAnnotationList(day);
                if (list)
                    host.appendChild(list);
                entry = { host, height: 0 };
                this.annotationEls.set(slot.date, entry);
                changed = true;
            }
            entry.host.style.top = `${Math.round(slot.top)}px`;
            entry.host.style.width = `${width}px`;
            if (entry.host.parentElement !== this.annotationsLayer) {
                this.annotationsLayer.appendChild(entry.host);
            }
            const measured = Math.ceil(entry.host.getBoundingClientRect().height);
            if (measured > 0 && measured !== entry.height) {
                entry.height = measured;
                changed = true;
            }
        }
        for (const [date, entry] of [...this.annotationEls]) {
            if (live.has(date))
                continue;
            entry.host.remove();
            this.annotationEls.delete(date);
        }
        return changed;
    }
    disposeAnnotationLists() {
        for (const entry of this.annotationEls.values())
            entry.host.remove();
        this.annotationEls.clear();
        this.annotationsLayer.replaceChildren();
    }
    /** The fixed HTML list of one day's annotations (icon, name, times, description). */
    buildAnnotationList(day) {
        if (day.annotations.length === 0)
            return null;
        const list = document.createElement("div");
        list.className = "tac-annotations";
        for (const annotation of day.annotations) {
            // A range that crosses local midnight is split into one piece per day so its band is drawn
            // on every row it covers. Only the first piece gets an annotation-list row; the continuation
            // pieces would otherwise repeat the same event under the following day.
            if (annotation.continued) {
                continue;
            }
            const row = document.createElement("div");
            row.className = "tac-annotation";
            row.dataset.type = annotation.type;
            const icon = document.createElement("span");
            icon.className = "tac-annotation-icon";
            const iconEl = this.annotationIcon(annotation.icon);
            if (iconEl)
                iconEl.style.color = annotation.color || "";
            if (iconEl)
                icon.appendChild(iconEl);
            const name = document.createElement("span");
            name.className = "tac-annotation-name";
            name.textContent = annotation.name;
            const times = document.createElement("span");
            times.className = "tac-annotation-times";
            times.textContent = annotationTimes(annotation, this.options.timezone);
            row.append(icon, name, times);
            if (annotation.description) {
                const description = document.createElement("span");
                description.className = "tac-annotation-description";
                description.textContent = annotation.description;
                row.appendChild(description);
            }
            list.appendChild(row);
        }
        return list;
    }
    /** Icon of a list entry: image URL, configured activity icon, or a drawn glyph. */
    annotationIcon(kind) {
        if (!kind)
            return null;
        if (typeof kind === "object" && "href" in kind) {
            const img = document.createElement("img");
            img.className = "tac-annotation-img";
            img.src = resolveIconHref(kind.href);
            img.alt = "";
            return img;
        }
        if (kind !== "warning" && this.options.icons?.[kind]) {
            const img = document.createElement("img");
            img.className = "tac-annotation-img";
            img.src = resolveIconHref(this.options.icons[kind]);
            img.alt = "";
            return img;
        }
        const svg = svgEl("svg", { viewBox: "0 0 12 12", width: 12, height: 12, "aria-hidden": "true" });
        if (kind === "warning") {
            svgEl("path", {
                d: "M6 1.2 11 10.4H1L6 1.2Z",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": 1.1,
                "stroke-linejoin": "round",
            }, svg);
            svgEl("line", { x1: 6, y1: 4.2, x2: 6, y2: 7.2, stroke: "currentColor", "stroke-width": 1.1 }, svg);
            svgEl("circle", { cx: 6, cy: 8.9, r: 0.7, fill: "currentColor" }, svg);
        }
        else {
            svgEl("rect", {
                x: 1,
                y: 3,
                width: 10,
                height: 6,
                rx: 1.5,
                fill: "currentColor",
                opacity: 0.85,
            }, svg);
        }
        return svg;
    }
    scheduleRender() {
        if (this.frame)
            return;
        this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.render();
        });
    }
    measureWidth() {
        const available = this.scroll.clientWidth || this.root.clientWidth || 0;
        return Math.max(this.options.minWidth, Math.round(available));
    }
    commitView(view, dayKey = null) {
        const span = clamp(view.to - view.from, MIN_SPAN, 1);
        let from = clamp(view.from, 0, 1 - span);
        const to = clamp(from + span, from + MIN_SPAN, 1);
        from = clamp(to - span, 0, 1 - MIN_SPAN);
        const next = { from, to };
        if (this.options.mode === "stacked" && dayKey) {
            const current = this.dayViews.get(dayKey) ?? FULL_VIEW;
            if (Math.abs(from - current.from) < 1e-6 && Math.abs(to - current.to) < 1e-6)
                return;
            if (span >= 0.999)
                this.dayViews.delete(dayKey);
            else
                this.dayViews.set(dayKey, next);
            this.activeDayKey = dayKey;
        }
        else if (this.options.mode === "stacked") {
            if (this.dayViews.size === 0 && this.view.from === 0 && this.view.to === 1 && span >= 0.999)
                return;
            this.view = next;
            this.dayViews.clear();
            this.activeDayKey = null;
            if (span < 0.999) {
                for (const day of this.model.days)
                    this.dayViews.set(day.dateKey, next);
                this.activeDayKey = this.model.days[0]?.dateKey ?? null;
            }
        }
        else {
            if (Math.abs(from - this.view.from) < 1e-6 && Math.abs(to - this.view.to) < 1e-6)
                return;
            this.view = next;
        }
        this.tooltipKey = null;
        this.hideTooltip();
        this.options.onViewChange?.({ ...next });
        this.root.dispatchEvent(new CustomEvent("tacho-viewchange", { detail: { ...next }, bubbles: true }));
        this.scheduleRender();
    }
    dayViewFor(dayKey) {
        return this.dayViews.get(dayKey) ?? FULL_VIEW;
    }
    dayKeyAtClientY(clientY) {
        if (this.options.mode !== "stacked" || !this.renderState)
            return null;
        const y = this.localY(clientY);
        for (const [key, geometry] of this.renderState.dayGeometry) {
            if (geometry.top === undefined || geometry.bottom === undefined)
                continue;
            if (y >= geometry.top && y <= geometry.bottom)
                return key;
        }
        return null;
    }
    fractionForInstant(instant) {
        if (this.options.mode === "continuous") {
            for (const day of this.model.days) {
                if (instant >= day.dayStart && instant < day.dayEnd) {
                    const offset = this.model.offsets.get(day.dateKey) ?? 0;
                    return (offset + (instant - day.dayStart) / 60) / Math.max(1, this.model.minutesTotal);
                }
            }
            if (this.model.days.length > 0) {
                return instant < this.model.days[0].dayStart ? 0 : 1;
            }
            return null;
        }
        const day = this.model.days[0];
        if (!day)
            return null;
        return clamp((instant - day.dayStart) / 60 / day.minutes, 0, 1);
    }
    /* ---------------- chrome ---------------- */
    applyThemeVars() {
        const theme = this.options.theme;
        const vars = {
            "--tac-bg": theme.background,
            "--tac-surface": theme.surface,
            "--tac-surface-alt": theme.surfaceAlt,
            "--tac-border": theme.border,
            "--tac-text": theme.text,
            "--tac-muted": theme.mutedText,
            "--tac-tip-bg": theme.tooltipBackground,
            "--tac-tip-text": theme.tooltipText,
            "--tac-tip-muted": theme.tooltipMuted,
            "--tac-focus": theme.focusRing,
            "--tac-selection": theme.selection,
        };
        for (const [name, value] of Object.entries(vars)) {
            this.root.style.setProperty(name, value);
        }
    }
    hasCountries() {
        return this.model.days.some((day) => day.countries.length > 0 || day.countryEvents.length > 0);
    }
    hasVehicles() {
        return this.model.days.some((day) => day.vehicles.length > 0);
    }
    hasCoDriverLane() {
        return this.model.days.some((day) => !!day.coDriverLane);
    }
    buildChrome() {
        const { legend, toolbar, strings, theme } = this.options;
        this.bar.hidden = !legend && !toolbar;
        this.legendEl.replaceChildren();
        this.actionsEl.replaceChildren();
        if (legend) {
            const chips = [
                { key: "driver", label: strings.driver, color: theme.driving },
                { key: "outOfScope", label: strings.outOfScope, color: theme.outOfScope },
                { key: "ferry", label: strings.ferry, color: theme.ferry },
                { key: "noCard", label: strings.noCard, color: theme.noCardStripe },
                { key: "totals", label: strings.totals, color: theme.gridStrong },
            ];
            if (this.hasCountries()) {
                chips.splice(1, 0, { key: "countries", label: strings.country, color: theme.country });
            }
            if (this.hasVehicles()) {
                chips.splice(1, 0, { key: "vehicles", label: strings.vehicle, color: theme.vehicle });
            }
            for (const chip of chips) {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "tac-chip";
                button.dataset.toggle = chip.key;
                button.title = chip.label;
                const dot = document.createElement("span");
                dot.className = "tac-dot";
                dot.style.background = chip.color;
                const label = document.createElement("span");
                label.textContent = chip.label;
                button.append(dot, label);
                this.legendEl.appendChild(button);
            }
            const noData = document.createElement("span");
            noData.className = "tac-chip tac-chip-static";
            noData.title = strings.noData;
            const noDataDot = document.createElement("span");
            noDataDot.className = "tac-dot";
            noDataDot.style.background = theme.noData;
            const noDataLabel = document.createElement("span");
            noDataLabel.textContent = strings.noData;
            noData.append(noDataDot, noDataLabel);
            this.legendEl.appendChild(noData);
        }
        if (toolbar) {
            if (this.hasCoDriverLane())
                this.addCoDriverControl();
            this.addAction("−", strings.zoomOut, "zoom-out");
            this.addAction("+", strings.zoomIn, "zoom-in");
            this.addAction(strings.reset, strings.resetZoom, "reset");
            this.addAction(strings.range, strings.selectRange, "brush");
            this.addAction(strings.exportSvg, strings.exportSvg, "export-svg");
            this.addAction(strings.exportPng, strings.exportPng, "export-png");
        }
        this.updateChromeState();
    }
    addCoDriverControl() {
        const strings = this.options.strings;
        const wrap = document.createElement("label");
        wrap.className = "tac-control";
        wrap.title = strings.coDriverDisplay;
        const caption = document.createElement("span");
        caption.className = "tac-control-label";
        caption.textContent = strings.coDriver;
        const select = document.createElement("select");
        select.className = "tac-select";
        select.setAttribute("aria-label", strings.coDriverDisplay);
        const choices = [
            ["auto", strings.coDriverAuto],
            ["always", strings.coDriverAlways],
            ["never", strings.coDriverNever],
        ];
        for (const [value, label] of choices) {
            const option = document.createElement("option");
            option.value = value;
            option.textContent = label;
            select.appendChild(option);
        }
        select.value = this.options.coDriver;
        select.addEventListener("change", () => {
            this.setCoDriverDisplay(select.value);
        });
        wrap.append(caption, select);
        this.actionsEl.appendChild(wrap);
    }
    addAction(label, title, action) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "tac-btn";
        button.dataset.action = action;
        button.textContent = label;
        button.title = title;
        button.setAttribute("aria-label", title);
        this.actionsEl.appendChild(button);
    }
    updateChromeState() {
        this.legendEl.querySelectorAll("button[data-toggle]").forEach((button) => {
            const key = button.dataset.toggle;
            if (!key)
                return;
            button.setAttribute("aria-pressed", String(this.visibility[key]));
        });
        const brushButton = this.actionsEl.querySelector('button[data-action="brush"]');
        if (brushButton)
            brushButton.setAttribute("aria-pressed", String(this.brushMode));
        if (this.svg)
            this.svg.classList.toggle("is-brush", this.brushMode);
        // With the toolbar disabled there is no reset action; expose an explicit
        // reset button at the top-right of the chart whenever a zoom is active.
        this.resetEl.hidden = this.options.toolbar || !this.isZoomed();
        this.resetEl.style.top = "8px";
    }
    isZoomed() {
        if (this.options.mode === "stacked") {
            return [...this.dayViews.values()].some((view) => view.from > 0.0001 || view.to < 0.9999);
        }
        return this.view.from > 0.0001 || this.view.to < 0.9999;
    }
    zoomBy(factor) {
        if (this.options.mode === "stacked") {
            const dayKey = this.activeDayKey ?? this.model.days[0]?.dateKey ?? null;
            if (!dayKey)
                return;
            this.applyZoom(this.dayViewFor(dayKey), 0.5, factor, dayKey);
            return;
        }
        this.applyZoom(this.view, 0.5, factor);
    }
    applyZoom(base, anchor, factor, dayKey = null) {
        const span = base.to - base.from;
        const anchorValue = base.from + anchor * span;
        const newSpan = clamp(span / factor, MIN_SPAN, 1);
        let from = anchorValue - anchor * newSpan;
        let to = from + newSpan;
        if (from < 0) {
            to -= from;
            from = 0;
        }
        if (to > 1) {
            from -= to - 1;
            to = 1;
        }
        from = clamp(from, 0, 1 - MIN_SPAN);
        to = clamp(to, from + MIN_SPAN, 1);
        this.commitView({ from, to }, dayKey);
    }
    capturePointer(pointerId) {
        try {
            this.canvas.setPointerCapture(pointerId);
        }
        catch {
            /* pointer capture is best-effort */
        }
    }
    releasePointer(pointerId) {
        try {
            this.canvas.releasePointerCapture(pointerId);
        }
        catch {
            /* pointer capture is best-effort */
        }
    }
    fractionAtClientX(clientX) {
        if (!this.renderState)
            return 0.5;
        const plot = this.renderState.plot;
        return clamp((this.localX(clientX) - plot.x) / plot.width, 0, 1);
    }
    localX(clientX) {
        if (!this.svg)
            return 0;
        const rect = this.svg.getBoundingClientRect();
        return ((clientX - rect.left) / Math.max(1, rect.width)) * this.svgWidth;
    }
    localY(clientY) {
        if (!this.svg || !this.renderState)
            return 0;
        const rect = this.svg.getBoundingClientRect();
        return ((clientY - rect.top) / Math.max(1, rect.height)) * this.renderState.height;
    }
    beginBrush(event) {
        if (!this.svg || !this.renderState)
            return;
        const x = this.localX(event.clientX);
        this.brushDayKey = this.dayKeyAtClientY(event.clientY);
        const geometry = this.brushDayKey ? this.renderState.dayGeometry.get(this.brushDayKey) : undefined;
        const top = geometry?.top ?? 0;
        const bottom = geometry?.bottom ?? this.renderState.height;
        const rect = document.createElementNS(SVG_NS, "rect");
        rect.setAttribute("x", String(x));
        rect.setAttribute("y", String(top));
        rect.setAttribute("width", "0");
        rect.setAttribute("height", String(Math.max(1, bottom - top)));
        rect.setAttribute("fill", this.options.theme.selection);
        rect.setAttribute("opacity", "0.14");
        rect.setAttribute("pointer-events", "none");
        this.svg.appendChild(rect);
        this.brushRect = rect;
        this.brushPointerId = event.pointerId;
        this.brushStartX = x;
        this.capturePointer(event.pointerId);
    }
    updateBrush(clientX) {
        if (!this.brushRect)
            return;
        const x = clamp(this.localX(clientX), this.renderState?.plot.x ?? 0, this.plotRight());
        const start = clamp(this.brushStartX, this.renderState?.plot.x ?? 0, this.plotRight());
        const left = Math.min(start, x);
        const width = Math.abs(x - start);
        this.brushRect.setAttribute("x", String(left));
        this.brushRect.setAttribute("width", String(width));
    }
    finishBrush(clientX) {
        const plot = this.renderState?.plot;
        const start = this.brushStartX;
        const end = this.localX(clientX);
        this.brushRect?.remove();
        this.brushRect = null;
        this.brushPointerId = null;
        const dayKey = this.brushDayKey;
        this.brushDayKey = null;
        if (!plot) {
            this.didDrag = false;
            return;
        }
        const x1 = Math.max(plot.x, Math.min(start, end));
        const x2 = Math.min(plot.x + plot.width, Math.max(start, end));
        if (x2 - x1 < 6) {
            // A click, not a range: let the click handler select the segment.
            this.didDrag = false;
            return;
        }
        this.didDrag = true;
        const base = dayKey ? this.dayViewFor(dayKey) : this.view;
        const span = base.to - base.from;
        const from = base.from + ((x1 - plot.x) / plot.width) * span;
        const to = base.from + ((x2 - plot.x) / plot.width) * span;
        this.commitView({ from, to }, dayKey);
    }
    plotRight() {
        const plot = this.renderState?.plot;
        return plot ? plot.x + plot.width : this.svgWidth;
    }
    /* ---------------- hover + tooltip ---------------- */
    handleHover(event) {
        const info = this.hitFromEvent(event);
        if (!info) {
            this.clearHover();
            return;
        }
        this.setHoveredElement(info.id);
        if (this.options.mode === "stacked")
            this.activeDayKey = info.hit.day.dateKey;
        if (info.id !== this.hoverKey) {
            this.hoverKey = info.id;
            const hover = this.hoverFromHit(info.hit);
            this.options.onHover?.(hover);
            this.root.dispatchEvent(new CustomEvent("tacho-hover", { detail: hover, bubbles: true }));
        }
        if (this.tooltipKey !== info.id) {
            this.tooltipKey = info.id;
            this.tip.innerHTML = this.tooltipHtml(info.hit);
        }
        this.tip.hidden = false;
        this.placeTooltip(event.clientX, event.clientY);
    }
    placeTooltip(clientX, clientY) {
        if (this.tip.hidden)
            return;
        const rect = this.tip.getBoundingClientRect();
        let left = clientX + TOOLTIP_OFFSET;
        let top = clientY + TOOLTIP_OFFSET;
        if (left + rect.width > window.innerWidth - 8) {
            left = clientX - rect.width - TOOLTIP_OFFSET;
        }
        if (top + rect.height > window.innerHeight - 8) {
            top = clientY - rect.height - TOOLTIP_OFFSET;
        }
        this.tip.style.left = `${Math.max(8, left)}px`;
        this.tip.style.top = `${Math.max(8, top)}px`;
    }
    clearHover() {
        if (this.hoverKey !== null) {
            this.hoverKey = null;
            this.options.onHover?.(null);
            this.root.dispatchEvent(new CustomEvent("tacho-hover", { detail: null, bubbles: true }));
        }
        this.hideTooltip();
    }
    hideTooltip() {
        this.tip.hidden = true;
        this.tooltipKey = null;
        this.hoverKey = null;
        this.setHoveredElement(null);
    }
    setHoveredElement(id) {
        if (this.hoveredElement?.getAttribute("data-hit") === id)
            return;
        this.hoveredElement?.classList.remove("is-hovered");
        this.hoveredElement = null;
        this.hoverOutline?.remove();
        this.hoverOutline = null;
        if (!id || !this.svg)
            return;
        const element = Array.from(this.svg.querySelectorAll("[data-hit]")).find((candidate) => candidate.getAttribute("data-hit") === id);
        if (!element)
            return;
        element.classList.add("is-hovered");
        this.hoveredElement = element;
        const shape = element.querySelector("rect");
        let box;
        try {
            box = shape ? shape.getBBox() : element.getBBox();
        }
        catch {
            return;
        }
        if (box.width <= 0 || box.height <= 0)
            return;
        const outline = document.createElementNS(SVG_NS, "rect");
        outline.setAttribute("class", "tac-hover-outline");
        outline.setAttribute("x", String(box.x));
        outline.setAttribute("y", String(box.y));
        outline.setAttribute("width", String(box.width));
        outline.setAttribute("height", String(box.height));
        const rx = shape?.getAttribute("rx");
        if (rx)
            outline.setAttribute("rx", rx);
        outline.setAttribute("fill", "none");
        outline.setAttribute("stroke", "#f8fafc");
        outline.setAttribute("stroke-width", "2");
        outline.setAttribute("stroke-opacity", "0.9");
        outline.setAttribute("pointer-events", "none");
        this.svg.appendChild(outline);
        this.hoverOutline = outline;
    }
    hitFromEvent(event) {
        const target = event.target;
        if (!(target instanceof Element))
            return null;
        return this.hitFromElement(target);
    }
    hitFromPoint(clientX, clientY) {
        const element = document.elementFromPoint(clientX, clientY);
        if (!(element instanceof Element))
            return null;
        return this.hitFromElement(element);
    }
    hitFromElement(element) {
        const hitElement = element.closest("[data-hit]");
        if (!hitElement)
            return null;
        const id = hitElement.getAttribute("data-hit");
        if (!id)
            return null;
        const hit = this.hits.get(id);
        return hit ? { id, hit } : null;
    }
    /* ---------------- selection ---------------- */
    selectHit(hit) {
        const id = hit?.kind === "segment" ? hit.segment.id : null;
        if (id === this.selectedId)
            return;
        const hadFocus = this.canvas.contains(document.activeElement);
        this.selectedId = id;
        this.hideTooltip();
        this.render();
        if (hadFocus && id) {
            const next = Array.from(this.svg?.querySelectorAll("[data-hit]") ?? []).find((element) => element.getAttribute("data-hit") === id);
            next?.focus({ preventScroll: true });
        }
        const selection = hit ? this.selectionFromHit(hit) : null;
        this.options.onSelect?.(selection);
        this.root.dispatchEvent(new CustomEvent("tacho-select", {
            detail: selection,
            bubbles: true,
        }));
    }
    selectionFromHit(hit) {
        if (hit.kind !== "segment")
            return null;
        const segment = hit.segment;
        return {
            kind: "segment",
            day: hit.day.dayStart,
            segment: segment.source,
            type: segment.type,
            activity: segment.kind,
            start: segment.start,
            end: segment.end,
            driverName: segment.driverName,
            cardNumber: segment.cardNumber,
            entryType: segment.entryType,
            manual: segment.manual,
            noCard: segment.noCard,
        };
    }
    hoverFromHit(hit) {
        if (hit.kind === "segment") {
            const selection = this.selectionFromHit(hit);
            if (selection)
                return selection;
        }
        if (hit.kind === "outOfScope") {
            return {
                kind: "outOfScope",
                day: hit.day.dayStart,
                start: hit.day.dayStart + hit.range.startMinute * 60,
                end: hit.day.dayStart + hit.range.endMinute * 60,
            };
        }
        if (hit.kind === "ferry") {
            return { kind: "ferry", day: hit.day.dayStart, time: hit.marker.epoch };
        }
        if (hit.kind === "highlight") {
            return {
                kind: "highlight",
                day: hit.day.dayStart,
                start: hit.range.start,
                end: hit.range.end,
                label: hit.range.label || this.options.strings.highlight,
            };
        }
        if (hit.kind === "marker") {
            return {
                kind: "marker",
                day: hit.day.dayStart,
                time: hit.marker.time,
                label: hit.marker.label || this.options.strings.marker,
            };
        }
        if (hit.kind === "driverPeriod") {
            return {
                kind: "driverPeriod",
                day: hit.day.dayStart,
                start: hit.range.start,
                end: hit.range.end,
                driverName: hit.range.driverName,
                cardNumber: hit.range.cardNumber,
            };
        }
        if (hit.kind === "countryEvent") {
            return {
                kind: "countryEvent",
                day: hit.day.dayStart,
                time: hit.event.time,
                country: hit.event.country,
                countryCode: hit.event.countryCode,
            };
        }
        if (hit.kind === "vehicle") {
            return {
                kind: "vehicle",
                day: hit.day.dayStart,
                start: hit.range.start,
                end: hit.range.end,
                registrationNumber: hit.range.registrationNumber,
                vehicleId: hit.range.vehicleId,
            };
        }
        if (hit.kind === "country") {
            return {
                kind: "country",
                day: hit.day.dayStart,
                start: hit.range.start,
                end: hit.range.end,
                country: hit.range.country,
                countryCode: hit.range.countryCode,
            };
        }
        if (hit.kind === "totals") {
            return { kind: "totals", day: hit.day.dayStart, totals: hit.totals };
        }
        return { kind: "outOfScope", day: 0, start: 0, end: 0 };
    }
    /* ---------------- tooltip content ---------------- */
    tooltipHtml(hit) {
        const timezone = this.options.timezone;
        const strings = this.options.strings;
        const locale = this.options.locale;
        const theme = this.options.theme;
        const dayLabel = formatDayLabel(hit.day.dayStart, timezone, locale);
        if (hit.kind === "segment") {
            const segment = hit.segment;
            const color = activityColor(theme, segment.kind);
            const badges = [];
            if (segment.noCard)
                badges.push(strings.noCard);
            if (segment.manual)
                badges.push(strings.manual);
            if (segment.entryType === "Card withdrawal")
                badges.push(strings.cardWithdrawal);
            const title = kindLabel(segment.kind, strings);
            const endClock = Math.abs(segment.end - hit.day.dayEnd) < 1 ? "24:00" : formatClock(segment.end, timezone);
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${color}"></span><span class="tac-tip-title">${escapeHtml(title)}</span>${badges
                    .map((badge) => `<span class="tac-badge">${escapeHtml(badge)}</span>`)
                    .join("")}</div>`,
                `<div class="tac-tip-time">${formatClock(segment.start, timezone)} – ${endClock}<span class="tac-tip-dur">${formatDuration((segment.end - segment.start) / 60)}</span></div>`,
                `<div class="tac-tip-rows">`,
                `<span class="tac-tip-k">${escapeHtml(strings.driverName)}</span><span class="tac-tip-v">${escapeHtml(segment.driverName || strings.unknownDriver)}</span>`,
                `<span class="tac-tip-k">${escapeHtml(strings.cardNumber)}</span><span class="tac-tip-v">${escapeHtml(segment.cardNumber || strings.unknownCard)}</span>`,
                `<span class="tac-tip-k">${escapeHtml(strings.entryType)}</span><span class="tac-tip-v">${escapeHtml(segment.entryType || "—")}</span>`,
                `</div>`,
            ].join("");
        }
        if (hit.kind === "outOfScope") {
            const start = hit.day.dayStart + hit.range.startMinute * 60;
            const end = hit.day.dayStart + hit.range.endMinute * 60;
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${theme.outOfScope}"></span><span class="tac-tip-title">${escapeHtml(strings.outOfScope)}</span></div>`,
                `<div class="tac-tip-time">${formatClock(start, timezone)} – ${formatClock(end, timezone)}<span class="tac-tip-dur">${formatDuration((end - start) / 60)}</span></div>`,
            ].join("");
        }
        if (hit.kind === "ferry") {
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${theme.ferry}"></span><span class="tac-tip-title">${escapeHtml(strings.ferry)}</span></div>`,
                `<div class="tac-tip-time">${formatClock(hit.marker.epoch, timezone)}</div>`,
            ].join("");
        }
        if (hit.kind === "highlight") {
            const range = hit.range;
            const label = range.label || strings.highlight;
            const start = range.start;
            const end = range.end;
            const totalMinutes = (range.periodEnd - range.periodStart) / 60;
            const clipped = range.start !== range.periodStart || range.end !== range.periodEnd;
            const clippedEnd = Math.abs(range.end - hit.day.dayEnd) < 1 ? "24:00" : formatClock(range.end, timezone);
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${range.color || theme.outOfScope}"></span><span class="tac-tip-title">${escapeHtml(label)}</span></div>`,
                `<div class="tac-tip-time">${formatClock(start, timezone)} – ${formatClock(end, timezone)}<span class="tac-tip-dur">${formatDuration(totalMinutes)}</span></div>`,
                clipped
                    ? `<div class="tac-tip-rows"><span class="tac-tip-k">${escapeHtml(strings.shown)}</span><span class="tac-tip-v">${formatClock(range.start, timezone)} – ${clippedEnd}</span></div>`
                    : "",
            ].join("");
        }
        if (hit.kind === "marker") {
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${hit.marker.color || theme.mutedText}"></span><span class="tac-tip-title">${escapeHtml(hit.marker.label || strings.marker)}</span></div>`,
                `<div class="tac-tip-time">${formatClock(hit.marker.time, timezone)}</div>`,
            ].join("");
        }
        if (hit.kind === "annotation") {
            const annotation = hit.annotation;
            const color = annotation.color || theme.selection || theme.mutedText;
            const time = annotation.type === "range" && annotation.start !== undefined && annotation.end !== undefined
                ? `${formatClock(annotation.start, timezone)} – ${formatClock(annotation.end, timezone)}<span class="tac-tip-dur">${formatDuration((annotation.end - annotation.start) / 60)}</span>`
                : annotation.start !== undefined
                    ? formatClock(annotation.start, timezone)
                    : "";
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${escapeHtml(color)}"></span><span class="tac-tip-title">${escapeHtml(annotation.name)}</span></div>`,
                time ? `<div class="tac-tip-time">${time}</div>` : "",
                annotation.description
                    ? `<div class="tac-tip-desc">${escapeHtml(annotation.description)}</div>`
                    : "",
            ].join("");
        }
        if (hit.kind === "driverPeriod") {
            const range = hit.range;
            const sameStartDay = localDateKey(range.periodStart, timezone) === hit.day.dateKey;
            const sameEndDay = localDateKey(range.periodEnd, timezone) === hit.day.dateKey;
            const startLabel = sameStartDay
                ? formatClock(range.periodStart, timezone)
                : `${formatDayLabel(range.periodStart, timezone, locale)}, ${formatClock(range.periodStart, timezone)}`;
            const endLabel = sameEndDay
                ? Math.abs(range.periodEnd - hit.day.dayEnd) < 1
                    ? "24:00"
                    : formatClock(range.periodEnd, timezone)
                : `${formatDayLabel(range.periodEnd, timezone, locale)}, ${formatClock(range.periodEnd, timezone)}`;
            const totalMinutes = (range.periodEnd - range.periodStart) / 60;
            const clipped = range.start !== range.periodStart || range.end !== range.periodEnd;
            const clippedEnd = Math.abs(range.end - hit.day.dayEnd) < 1 ? "24:00" : formatClock(range.end, timezone);
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${theme.selection}"></span><span class="tac-tip-title">${escapeHtml(range.driverName)}</span><span class="tac-badge">${escapeHtml(strings.driver)}</span></div>`,
                `<div class="tac-tip-time">${escapeHtml(startLabel)} – ${escapeHtml(endLabel)}<span class="tac-tip-dur">${formatDuration(totalMinutes)}</span></div>`,
                `<div class="tac-tip-rows">`,
                range.cardNumber
                    ? `<span class="tac-tip-k">${escapeHtml(strings.cardNumber)}</span><span class="tac-tip-v">${escapeHtml(range.cardNumber)}</span>`
                    : "",
                `<span class="tac-tip-k">${escapeHtml(strings.entered)}</span><span class="tac-tip-v">${escapeHtml(startLabel)}</span>`,
                `<span class="tac-tip-k">${escapeHtml(strings.left)}</span><span class="tac-tip-v">${escapeHtml(endLabel)}</span>`,
                clipped
                    ? `<span class="tac-tip-k">${escapeHtml(strings.shown)}</span><span class="tac-tip-v">${formatClock(range.start, timezone)} – ${clippedEnd}</span>`
                    : "",
                `</div>`,
            ].join("");
        }
        if (hit.kind === "countryEvent") {
            const event = hit.event;
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${countryColor(event.country)}"></span><span class="tac-tip-title">${escapeHtml(event.country || strings.unknownCountry)}</span><span class="tac-badge">${escapeHtml(strings.country)}</span></div>`,
                `<div class="tac-tip-time">${escapeHtml(strings.entered)} ${formatClock(event.time, timezone)}</div>`,
                event.countryCode
                    ? `<div class="tac-tip-rows"><span class="tac-tip-k">${escapeHtml(strings.countryCode)}</span><span class="tac-tip-v">${escapeHtml(event.countryCode)}</span></div>`
                    : "",
            ].join("");
        }
        if (hit.kind === "vehicle") {
            const range = hit.range;
            const sameStartDay = localDateKey(range.periodStart, timezone) === hit.day.dateKey;
            const sameEndDay = localDateKey(range.periodEnd, timezone) === hit.day.dateKey;
            const startLabel = sameStartDay
                ? formatClock(range.periodStart, timezone)
                : `${formatDayLabel(range.periodStart, timezone, locale)}, ${formatClock(range.periodStart, timezone)}`;
            const endLabel = sameEndDay
                ? Math.abs(range.periodEnd - hit.day.dayEnd) < 1
                    ? "24:00"
                    : formatClock(range.periodEnd, timezone)
                : `${formatDayLabel(range.periodEnd, timezone, locale)}, ${formatClock(range.periodEnd, timezone)}`;
            const totalMinutes = (range.periodEnd - range.periodStart) / 60;
            const clipped = range.start !== range.periodStart || range.end !== range.periodEnd;
            const clippedEnd = Math.abs(range.end - hit.day.dayEnd) < 1 ? "24:00" : formatClock(range.end, timezone);
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${theme.vehicle}"></span><span class="tac-tip-title">${escapeHtml(range.registrationNumber || strings.unknownVehicle)}</span><span class="tac-badge">${escapeHtml(strings.vehicle)}</span></div>`,
                `<div class="tac-tip-time">${escapeHtml(startLabel)} – ${escapeHtml(endLabel)}<span class="tac-tip-dur">${formatDuration(totalMinutes)}</span></div>`,
                `<div class="tac-tip-rows">`,
                `<span class="tac-tip-k">${escapeHtml(strings.entered)}</span><span class="tac-tip-v">${escapeHtml(startLabel)}</span>`,
                `<span class="tac-tip-k">${escapeHtml(strings.left)}</span><span class="tac-tip-v">${escapeHtml(endLabel)}</span>`,
                clipped
                    ? `<span class="tac-tip-k">${escapeHtml(strings.shown)}</span><span class="tac-tip-v">${formatClock(range.start, timezone)} – ${clippedEnd}</span>`
                    : "",
                range.vehicleId
                    ? `<span class="tac-tip-k">${escapeHtml(strings.vin)}</span><span class="tac-tip-v">${escapeHtml(range.vehicleId)}</span>`
                    : "",
                `</div>`,
            ].join("");
        }
        if (hit.kind === "country") {
            const accent = countryColor(hit.range.country);
            const range = hit.range;
            const sameStartDay = localDateKey(range.periodStart, timezone) === hit.day.dateKey;
            const sameEndDay = localDateKey(range.periodEnd, timezone) === hit.day.dateKey;
            const startLabel = sameStartDay
                ? formatClock(range.periodStart, timezone)
                : `${formatDayLabel(range.periodStart, timezone, locale)}, ${formatClock(range.periodStart, timezone)}`;
            const endLabel = sameEndDay
                ? Math.abs(range.periodEnd - hit.day.dayEnd) < 1
                    ? "24:00"
                    : formatClock(range.periodEnd, timezone)
                : `${formatDayLabel(range.periodEnd, timezone, locale)}, ${formatClock(range.periodEnd, timezone)}`;
            const totalMinutes = (range.periodEnd - range.periodStart) / 60;
            const clipped = range.start !== range.periodStart || range.end !== range.periodEnd;
            const clippedEnd = Math.abs(range.end - hit.day.dayEnd) < 1 ? "24:00" : formatClock(range.end, timezone);
            return [
                `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
                `<div class="tac-tip-head"><span class="tac-dot" style="background:${accent}"></span><span class="tac-tip-title">${escapeHtml(range.country || strings.unknownCountry)}</span><span class="tac-badge">${escapeHtml(strings.country)}</span></div>`,
                `<div class="tac-tip-time">${escapeHtml(startLabel)} – ${escapeHtml(endLabel)}<span class="tac-tip-dur">${formatDuration(totalMinutes)}</span></div>`,
                `<div class="tac-tip-rows">`,
                `<span class="tac-tip-k">${escapeHtml(strings.entered)}</span><span class="tac-tip-v">${escapeHtml(startLabel)}</span>`,
                `<span class="tac-tip-k">${escapeHtml(strings.left)}</span><span class="tac-tip-v">${escapeHtml(endLabel)}</span>`,
                clipped
                    ? `<span class="tac-tip-k">${escapeHtml(strings.shown)}</span><span class="tac-tip-v">${formatClock(range.start, timezone)} – ${clippedEnd}</span>`
                    : "",
                range.countryCode
                    ? `<span class="tac-tip-k">${escapeHtml(strings.countryCode)}</span><span class="tac-tip-v">${escapeHtml(range.countryCode)}</span>`
                    : "",
                `</div>`,
            ].join("");
        }
        const entries = ["driving", "work", "rest", "availability", "noCard"];
        const sum = entries.reduce((acc, kind) => acc + hit.totals[kind], 0);
        const inView = this.view.to - this.view.from < 0.999;
        const rows = entries
            .filter((kind) => hit.totals[kind] > 0.5)
            .map((kind) => {
            const color = kind === "noCard" ? theme.noCard : activityColor(theme, kind);
            const value = formatDurationCompact(hit.totals[kind]);
            return `<div class="tac-tip-total"><span class="tac-dot" style="background:${color}"></span><span>${escapeHtml(kindLabel(kind, strings))}</span><span class="tac-tip-total-value">${value}</span></div>`;
        })
            .join("");
        return [
            `<div class="tac-tip-day">${escapeHtml(dayLabel)}</div>`,
            `<div class="tac-tip-head"><span class="tac-tip-title">${escapeHtml(inView ? strings.totalsInView : strings.totals)}</span></div>`,
            `<div class="tac-tip-totals">${rows || `<span class="tac-tip-k">${escapeHtml(strings.noActivity)}</span>`}</div>`,
            `<div class="tac-tip-total-sum"><span class="tac-tip-k">${escapeHtml(strings.total)}</span><span>${formatDuration(sum)}</span></div>`,
        ].join("");
    }
    /* ---------------- overview navigator ---------------- */
    navSvgWidth() {
        const width = Number(this.navSvg?.getAttribute("width"));
        return Number.isFinite(width) && width > 0 ? width : 1;
    }
    beginDayNav(event) {
        if (!this.renderState)
            return;
        const target = event.target;
        if (!(target instanceof Element))
            return;
        const dayElement = target.closest("[data-day-nav]");
        const dayKey = dayElement?.getAttribute("data-day-nav-key") ?? this.dayKeyAtClientY(event.clientY);
        if (!dayKey)
            return;
        const base = this.dayViewFor(dayKey);
        const span = base.to - base.from;
        if (span >= 0.999)
            return;
        this.activeDayKey = dayKey;
        const plot = this.renderState.plot;
        const handle = target.closest("[data-day-nav-handle]");
        const windowElement = target.closest("[data-day-nav-window]");
        const fraction = clamp((this.localX(event.clientX) - plot.x) / Math.max(1, plot.width), 0, 1);
        let nextView = null;
        if (!handle && !windowElement) {
            const from = clamp(fraction - span / 2, 0, 1 - span);
            nextView = { from, to: from + span };
            this.commitView(nextView, dayKey);
        }
        this.navDrag = {
            source: "canvas",
            pointerId: event.pointerId,
            mode: handle ? handle.getAttribute("data-day-nav-handle") : "pan",
            startClientX: event.clientX,
            unitWidth: plot.width,
            startView: nextView ?? base,
            dayKey,
        };
        this.capturePointer(event.pointerId);
    }
    applyNavDrag(clientX) {
        if (!this.navDrag)
            return;
        const delta = (clientX - this.navDrag.startClientX) / Math.max(1, this.navDrag.unitWidth);
        const base = this.navDrag.startView;
        const span = base.to - base.from;
        if (this.navDrag.mode === "pan") {
            const from = clamp(base.from + delta, 0, 1 - span);
            this.commitView({ from, to: from + span }, this.navDrag.dayKey);
            return;
        }
        if (this.navDrag.mode === "start") {
            const from = clamp(base.from + delta, 0, base.to - MIN_SPAN);
            this.commitView({ from, to: base.to }, this.navDrag.dayKey);
            return;
        }
        const to = clamp(base.to + delta, base.from + MIN_SPAN, 1);
        this.commitView({ from: base.from, to }, this.navDrag.dayKey);
    }
    navFractionAt(clientX) {
        const rect = this.navSvg?.getBoundingClientRect();
        if (!rect || rect.width <= 0)
            return 0.5;
        return clamp((clientX - rect.left) / rect.width, 0, 1);
    }
    buildNavigator() {
        const total = this.model.minutesTotal;
        const span = this.view.to - this.view.from;
        if (this.options.mode === "stacked" ||
            !this.options.navigator ||
            this.model.days.length === 0 ||
            total <= 0 ||
            span >= 0.999) {
            this.nav.hidden = true;
            this.navSvg = null;
            if (this.navDrag?.source === "bar")
                this.navDrag = null;
            return;
        }
        this.nav.hidden = false;
        const width = Math.max(240, Math.round((this.root.clientWidth || this.options.minWidth) - 20));
        const showVehicles = this.visibility.vehicles && this.hasVehicles();
        const height = showVehicles ? 42 : 36;
        const stripH = showVehicles ? 30 : 24;
        const stripBottom = 2 + stripH;
        const vehicleY = 15;
        const countryY = showVehicles ? 21 : 15;
        const labelY = height - 2;
        const navFont = 10.5;
        const pad = 2;
        const firstDay = this.model.days[0];
        const lastDay = this.model.days[this.model.days.length - 1];
        if (!firstDay || !lastDay) {
            this.nav.hidden = true;
            this.navSvg = null;
            if (this.navDrag?.source === "bar")
                this.navDrag = null;
            return;
        }
        const startEpoch = firstDay.dayStart + this.view.from * total * 60;
        const endEpoch = firstDay.dayStart + this.view.to * total * 60;
        const startDayLabel = formatDayLabel(startEpoch, this.options.timezone, this.options.locale);
        const endDayLabel = formatDayLabel(endEpoch, this.options.timezone, this.options.locale);
        const startClock = formatClock(startEpoch, this.options.timezone);
        const endClock = this.view.to >= 0.999 ? "24:00" : formatClock(endEpoch, this.options.timezone);
        const sameDay = localDateKey(startEpoch, this.options.timezone) ===
            localDateKey(endEpoch, this.options.timezone);
        const rangeText = sameDay
            ? `${startDayLabel}, ${startClock} – ${endClock}`
            : `${startDayLabel}, ${startClock} – ${endDayLabel}, ${endClock}`;
        const head = document.createElement("div");
        head.className = "tac-nav-head";
        const title = document.createElement("span");
        title.className = "tac-nav-title";
        title.textContent = this.options.strings.overview;
        const range = document.createElement("span");
        range.className = "tac-nav-range";
        range.textContent = rangeText;
        head.append(title, range);
        const svg = svgEl("svg", {
            width,
            height,
            viewBox: `0 0 ${width} ${height}`,
            class: "tac-nav-svg",
            role: "group",
            "aria-label": `${this.options.strings.overview}: ${rangeText}`,
        });
        const navX = (globalMinute) => pad + (globalMinute / total) * (width - pad * 2);
        svgEl("rect", { x: 0, y: 2, width, height: stripH, rx: 3, fill: this.options.theme.noData }, svg);
        const drawWindow = (x1, x2) => {
            const group = svgEl("g", {
                "data-nav-window": "1",
                class: "tac-nav-window",
            }, svg);
            svgEl("rect", {
                x: x1,
                y: 1,
                width: Math.max(2, x2 - x1),
                height: stripH + 2,
                rx: 2,
                fill: this.options.theme.selection,
                "fill-opacity": 0.06,
                stroke: this.options.theme.selection,
                "stroke-width": 1.4,
            }, group);
            svgEl("rect", {
                "data-nav-handle": "start",
                class: "tac-nav-handle",
                x: x1 - 4,
                y: 0,
                width: 8,
                height: stripH + 4,
                rx: 2,
                fill: this.options.theme.selection,
            }, group);
            svgEl("rect", {
                "data-nav-handle": "end",
                class: "tac-nav-handle",
                x: x2 - 4,
                y: 0,
                width: 8,
                height: stripH + 4,
                rx: 2,
                fill: this.options.theme.selection,
            }, group);
        };
        const drawDim = (parent, x, w) => {
            if (w <= 0)
                return;
            svgEl("rect", {
                x,
                y: 2,
                width: w,
                height: stripH,
                fill: this.options.theme.background,
                opacity: 0.55,
            }, parent);
        };
        for (const day of this.model.days) {
            const offset = this.model.offsets.get(day.dateKey) ?? 0;
            const dayX = navX(offset);
            const dayW = navX(offset + day.minutes) - dayX;
            const dayGroup = svgEl("g", {
                "data-nav-day": day.dateKey,
                "data-nav-day-start": dayX,
                "data-nav-day-width": dayW,
            }, svg);
            if (this.visibility.driver && day.lane) {
                for (const segment of day.lane.segments) {
                    if (!this.visibility.noCard && segment.noCard)
                        continue;
                    const x1 = navX(offset + segment.startMinute);
                    const x2 = navX(offset + segment.endMinute);
                    svgEl("rect", {
                        x: x1,
                        y: 4,
                        width: Math.max(0.4, x2 - x1),
                        height: 9,
                        fill: activityColor(this.options.theme, segment.kind),
                        opacity: segment.noCard ? 0.7 : 0.85,
                    }, dayGroup);
                }
            }
            if (showVehicles) {
                for (const vehicle of day.vehicles) {
                    const x1 = navX(offset + vehicle.startMinute);
                    const x2 = navX(offset + vehicle.endMinute);
                    svgEl("rect", {
                        x: x1,
                        y: vehicleY,
                        width: Math.max(0.4, x2 - x1),
                        height: 5,
                        fill: this.options.theme.vehicle,
                        opacity: 0.75,
                    }, dayGroup);
                }
            }
            if (this.visibility.countries) {
                for (const country of day.countries) {
                    const x1 = navX(offset + country.startMinute);
                    const x2 = navX(offset + country.endMinute);
                    svgEl("rect", {
                        x: x1,
                        y: countryY,
                        width: Math.max(0.4, x2 - x1),
                        height: 5,
                        fill: countryColor(country.country),
                        opacity: 0.7,
                    }, dayGroup);
                }
            }
            svgEl("line", {
                x1: dayX,
                y1: 2,
                x2: dayX,
                y2: stripBottom,
                stroke: this.options.theme.border,
                "stroke-width": 1,
            }, dayGroup);
            svgEl("rect", {
                x: dayX,
                y: 2,
                width: dayW,
                height: stripH,
                fill: "transparent",
            }, dayGroup);
        }
        const windowX1 = navX(this.view.from * total);
        const windowX2 = navX(this.view.to * total);
        drawDim(svg, 0, windowX1);
        drawDim(svg, windowX2, width - windowX2);
        drawWindow(windowX1, windowX2);
        if (width >= 300) {
            const startText = svgEl("text", {
                x: 2,
                y: labelY,
                fill: this.options.theme.axisText,
                "font-size": navFont,
            }, svg);
            startText.textContent = formatDayLabel(firstDay.dayStart, this.options.timezone, this.options.locale);
            const endText = svgEl("text", {
                x: width - 2,
                y: labelY,
                fill: this.options.theme.axisText,
                "font-size": navFont,
                "text-anchor": "end",
            }, svg);
            endText.textContent = formatDayLabel(lastDay.dayStart, this.options.timezone, this.options.locale);
        }
        this.nav.replaceChildren(head, svg);
        this.navSvg = svg;
    }
}
/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
function resolveOptions(raw) {
    const strings = resolveStrings(raw.strings);
    const dataSource = raw.dataSource === "driver" || raw.dataSource === "vehicle"
        ? raw.dataSource
        : undefined;
    if (dataSource === "driver") {
        // The driver endpoint has no vehicle card slot: "no card" there means the
        // tachograph did not attribute the time. Keep explicit caller overrides.
        if (raw.strings?.withDriverCard === undefined)
            strings.withDriverCard = strings.withTachograph;
        if (raw.strings?.withoutDriverCard === undefined)
            strings.withoutDriverCard = strings.withoutTachograph;
    }
    return {
        data: raw.data ?? { days: [] },
        timezone: raw.timezone,
        mode: raw.mode ?? "stacked",
        dataSource,
        locale: raw.locale ?? "en-GB",
        strings,
        theme: resolveTheme(raw.theme),
        minWidth: Math.max(320, raw.minWidth ?? 760),
        laneHeight: Math.max(70, raw.laneHeight ?? 70),
        barHeightMode: raw.barHeightMode ?? "specific",
        coDriver: raw.coDriver === "always" || raw.coDriver === "never" ? raw.coDriver : "auto",
        icons: raw.icons ?? {},
        showSegmentLabels: raw.showSegmentLabels ?? true,
        showDayMeta: raw.showDayMeta ?? true,
        showRulers: raw.showRulers ?? true,
        wheelZoom: raw.wheelZoom ?? false,
        toolbar: raw.toolbar ?? true,
        legend: raw.legend ?? true,
        navigator: raw.navigator ?? true,
        interactive: raw.interactive ?? true,
        ariaLabel: raw.ariaLabel ?? strings.chartLabel,
        onSelect: raw.onSelect,
        onHover: raw.onHover,
        onViewChange: raw.onViewChange,
    };
}
function assertTimeZone(timeZone) {
    if (!timeZone || typeof timeZone !== "string") {
        throw new RangeError("A valid IANA timezone is required");
    }
    try {
        new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    }
    catch {
        throw new RangeError(`Invalid IANA timezone: ${timeZone}`);
    }
}
function kindLabel(kind, strings) {
    switch (kind) {
        case "driving":
            return strings.driving;
        case "work":
            return strings.work;
        case "rest":
            return strings.rest;
        case "availability":
            return strings.availability;
        default:
            return strings.noCard;
    }
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/** `HH:mm` for a timestamp, `HH:mm – HH:mm` for a range, empty for `none`. */
function annotationTimes(annotation, timeZone) {
    if (annotation.type === "timestamp" && annotation.start !== undefined) {
        return formatClock(annotation.start, timeZone);
    }
    if (annotation.type === "range" && annotation.start !== undefined && annotation.end !== undefined) {
        return `${formatClock(annotation.start, timeZone)} – ${formatClock(annotation.end, timeZone)}`;
    }
    return "";
}
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Failed to rasterise chart"));
        image.src = url;
    });
}
function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
